import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Pagination,
  Stack,
  Box,
  Dialog,
  DialogContent,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { baseurl } from "../../Basurl/Baseurl";

const STATUSES = [
  "New",
  "Pending",
  "In Process",
  "Picked Up",
  "On the Way",
  "Dropped",
  "Completed",
  "Cancelled",
];

const STATUS_FILTERS = [
  { key: "All", label: "All", countKey: "total" },
  { key: "New", label: "New", countKey: "new" },
  { key: "Pending", label: "Pending", countKey: "pending" },
  { key: "In Process", label: "In Process", countKey: "inProcess" },
  { key: "Picked Up", label: "Picked Up", countKey: "pickedUp" },
  { key: "On the Way", label: "On the Way", countKey: "onTheWay" },
  { key: "Dropped", label: "Dropped", countKey: "dropped" },
  { key: "Completed", label: "Completed", countKey: "completed" },
  { key: "Cancelled", label: "Cancelled", countKey: "cancelled" },
];

const STATUS_COLORS = {
  New: { bg: "#eef2ff", color: "#3730a3" },
  Pending: { bg: "#fff7ed", color: "#c2410c" },
  "In Process": { bg: "#eff6ff", color: "#1d4ed8" },
  "Picked Up": { bg: "#ecfeff", color: "#0e7490" },
  "On the Way": { bg: "#f5f3ff", color: "#6d28d9" },
  Dropped: { bg: "#fdf4ff", color: "#a21caf" },
  Completed: { bg: "#ecfdf5", color: "#047857" },
  Cancelled: { bg: "#fef2f2", color: "#b91c1c" },
};

const DETAIL_FIELDS = [
  "driver_name",
  "driver_contact",
  "vehicle_no",
  "pickup_time",
  "drop_time",
  "pickup_location",
  "drop_location",
  "notes",
];

const emptyForm = {
  status: "Pending",
  driver_name: "",
  driver_contact: "",
  vehicle_no: "",
  pickup_time: "",
  drop_time: "",
  pickup_location: "",
  drop_location: "",
  notes: "",
};

const LIMIT = 25;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  "Content-Type": "application/json",
});

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
};

const formFromRow = (row) => ({
  status: row?.pickDropStatus || "Pending",
  driver_name: row?.driver_name || "",
  driver_contact: row?.driver_contact || "",
  vehicle_no: row?.vehicle_no || "",
  pickup_time: row?.pickup_time || "",
  drop_time: row?.drop_time || "",
  pickup_location: row?.pickup_location || "",
  drop_location: row?.drop_location || "",
  notes: row?.notes || "",
});

const InfoItem = ({ label, value }) => (
  <div>
    <h6>{label}</h6>
    <p>{value || "-"}</p>
  </div>
);

export default function PickDropApp() {
  const isReferral = (localStorage.getItem("Role") || "").toUpperCase() === "REFERRAL";

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("All");
  const [today, setToday] = useState(false);
  const [date, setDate] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [patientInput, setPatientInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedPatientId, setAppliedPatientId] = useState("");
  const [updatingId, setUpdatingId] = useState("");
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
      setAppliedPatientId(patientInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, patientInput]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        params.set("status", status || "All");
        params.set("page", String(page));
        params.set("limit", String(LIMIT));
        if (today) params.set("today", "true");
        if (date) params.set("date", date);
        if (appliedPatientId) params.set("patientId", appliedPatientId);
        if (appliedSearch) params.set("search", appliedSearch);

        const response = await axios.get(
          `${baseurl}get_pick_drop_appointments?${params.toString()}`,
          { headers: authHeaders(), signal: controller.signal },
        );
        const body = response?.data || {};
        setRows(Array.isArray(body.data) ? body.data : []);
        setCounts(body.counts || {});
        setTotal(Number(body.total ?? body.counts?.total ?? 0));
        setTotalPages(Math.max(1, Number(body.totalPages) || 1));
      } catch (err) {
        if (axios.isCancel?.(err) || err?.code === "ERR_CANCELED") return;
        setRows([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load pick and drop appointments",
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [status, today, date, appliedSearch, appliedPatientId, page, reloadKey]);

  const updateStatus = async (row, nextStatus, extra = null) => {
    if (isReferral) return;
    const appointmentId = row?.appointmentId;
    if (!appointmentId) {
      Swal.fire("Error", "Appointment id is missing", "error");
      return;
    }

    const payload = extra || { status: nextStatus };
    if (!payload.status && !payload.pickDropStatus) {
      payload.status = nextStatus;
    }

    setUpdatingId(appointmentId);
    try {
      const response = await axios.post(
        `${baseurl}update_pick_drop_status/${encodeURIComponent(appointmentId)}`,
        payload,
        { headers: authHeaders() },
      );
      if (response?.data?.success === false) {
        throw new Error(response?.data?.message || "Update failed");
      }
      Swal.fire(
        "Success",
        response?.data?.message || "Pick & Drop details updated successfully",
        "success",
      );
      setEditRow(null);
      setReloadKey((current) => current + 1);
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || err?.message || "Something went wrong",
        "error",
      );
    } finally {
      setUpdatingId("");
    }
  };

  const openEdit = (row) => {
    if (isReferral) return;
    setEditRow(row);
    setForm(formFromRow(row));
  };

  const handleSave = (event) => {
    event.preventDefault();
    if (!form.status) {
      Swal.fire("Error", "Status is required", "error");
      return;
    }
    const payload = { status: form.status };
    DETAIL_FIELDS.forEach((key) => {
      const next = String(form[key] || "").trim();
      const previous = String(editRow?.[key] || "").trim();
      if (next !== previous) payload[key] = next;
    });
    setSaving(true);
    updateStatus(editRow, form.status, payload).finally(() => setSaving(false));
  };

  const chipStyle = (active) => ({
    padding: "6px 12px",
    borderRadius: 20,
    border: active ? "1px solid #22c7b8" : "1px solid #e5e7eb",
    background: active ? "#22c7b8" : "#fff",
    color: active ? "#fff" : "#475569",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  return (
    <>
      <div className="country-top">
        <div className="topmainhd mb-0">
          <h6>Portal</h6>
        </div>
        <div className="search-btn-main" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <TextField
            label="Search"
            size="small"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Name, driver, vehicle, hospital"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchInput("")} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            label="Patient ID"
            size="small"
            value={patientInput}
            onChange={(event) => setPatientInput(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Date"
            type="date"
            size="small"
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
              setToday(false);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
          />
          <button
            type="button"
            onClick={() => {
              setToday((current) => !current);
              setDate("");
              setPage(1);
            }}
            style={chipStyle(today)}
          >
            Today
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          margin: "12px 0 16px",
        }}
      >
        {STATUS_FILTERS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => {
              setStatus(item.key);
              setPage(1);
            }}
            style={chipStyle(status === item.key)}
          >
            {item.label}
            <span style={{ marginLeft: 6, fontWeight: 700 }}>
              {counts?.[item.countKey] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}

      <div className="table-responsive">
        <TableContainer component={Paper} style={{ overflowX: "auto" }}>
          <Table stickyHeader className="table-no-card">
            <TableHead>
              <TableRow>
                <TableCell>Sr No.</TableCell>
                <TableCell>Appointment</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Hospital</TableCell>
                <TableCell>Date / Time</TableCell>
                <TableCell>Pickup</TableCell>
                <TableCell>Drop</TableCell>
                <TableCell>Driver / Vehicle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((item, index) => {
                  const tone = STATUS_COLORS[item.pickDropStatus] || {
                    bg: "#f8fafc",
                    color: "#334155",
                  };
                  return (
                    <TableRow key={item.appointmentId || item._id || index}>
                      <TableCell>{(page - 1) * LIMIT + index + 1}</TableCell>
                      <TableCell>
                        <div>{item.appointmentId || "-"}</div>
                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {item.appointmentStatus || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{item.patientName || "-"}</div>
                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {item.patientId || "-"}
                        </div>
                      </TableCell>
                      <TableCell>{item.patientPhone || "-"}</TableCell>
                      <TableCell>{item.hospitalName || "-"}</TableCell>
                      <TableCell>
                        <div>{formatDate(item.appointment_Date)}</div>
                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {item.appointment_Time || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{item.pickup_location || "-"}</div>
                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {item.pickup_time || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{item.drop_location || "-"}</div>
                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {item.drop_time || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{item.driver_name || "-"}</div>
                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {[item.driver_contact, item.vehicle_no].filter(Boolean).join(" · ") || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" className="cont-main" sx={{ minWidth: 140 }}>
                          <Select
                            value={item.pickDropStatus || ""}
                            displayEmpty
                            disabled={isReferral || updatingId === item.appointmentId}
                            onChange={(event) => updateStatus(item, event.target.value)}
                            className="status-direct"
                            sx={{
                              backgroundColor: tone.bg,
                              color: tone.color,
                              fontWeight: 600,
                              "& .MuiSelect-select": { py: "6px" },
                            }}
                          >
                            {STATUSES.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <div className="action-icon">
                          <VisibilityIcon
                            className="eye-icon"
                            style={{ cursor: "pointer" }}
                            onClick={() => setViewRow(item)}
                          />
                          {!isReferral && (
                            <i
                              className="fa-solid fa-pen-to-square"
                              title="Update trip"
                              style={{ cursor: "pointer" }}
                              onClick={() => openEdit(item)}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                !loading && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No Data Found
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            marginTop={2}
            paddingX={2}
            paddingBottom={2}
          >
            <span style={{ fontSize: 12, color: "#64748b" }}>{total} appointments</span>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              shape="rounded"
            />
          </Stack>
        </TableContainer>
      </div>

      <Dialog fullWidth maxWidth="md" open={Boolean(viewRow)} onClose={() => setViewRow(null)}>
        <div className="main-card-header">
          <div className="top-fixed-hd">
            <div className="note-hd">
              <h6>Pick & Drop Appointment</h6>
            </div>
            <div className="cross-icon" onClick={() => setViewRow(null)}>
              <i className="fa-solid fa-xmark"></i>
            </div>
          </div>
        </div>
        <DialogContent className="main-box view-table-detail">
          {viewRow && (
            <Box>
              <div className="all-hd mb-3">
                <h6>Patient</h6>
              </div>
              <div className="card mb-3">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <InfoItem label="Appointment ID" value={viewRow.appointmentId} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Patient" value={viewRow.patientName} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Patient ID" value={viewRow.patientId} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Phone" value={viewRow.patientPhone} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Hospital" value={viewRow.hospitalName} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Appointment Status" value={viewRow.appointmentStatus} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="all-hd mb-3">
                <h6>Trip</h6>
              </div>
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4">
                      <InfoItem label="Appointment Date" value={formatDate(viewRow.appointment_Date)} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Appointment Time" value={viewRow.appointment_Time} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Pick & Drop Status" value={viewRow.pickDropStatus} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Pickup Location" value={viewRow.pickup_location} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Pickup Time" value={viewRow.pickup_time} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Drop Location" value={viewRow.drop_location} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Drop Time" value={viewRow.drop_time} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Driver" value={viewRow.driver_name} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Driver Contact" value={viewRow.driver_contact} />
                    </div>
                    <div className="col-md-4">
                      <InfoItem label="Vehicle No." value={viewRow.vehicle_no} />
                    </div>
                    <div className="col-md-12">
                      <InfoItem label="Notes" value={viewRow.notes} />
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog fullWidth maxWidth="md" open={Boolean(editRow)} onClose={() => !saving && setEditRow(null)}>
        <div className="main-card-header">
          <div className="top-fixed-hd">
            <div className="note-hd">
              <h6>Update Pick & Drop</h6>
            </div>
            <div className="cross-icon" onClick={() => !saving && setEditRow(null)}>
              <i className="fa-solid fa-xmark"></i>
            </div>
          </div>
        </div>
        <DialogContent>
          <form onSubmit={handleSave}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={form.status}
                  onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
                  required
                >
                  {STATUSES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Driver Name</label>
                <input
                  className="form-control"
                  value={form.driver_name}
                  onChange={(event) => setForm((current) => ({ ...current, driver_name: event.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Driver Contact</label>
                <input
                  className="form-control"
                  value={form.driver_contact}
                  onChange={(event) => setForm((current) => ({ ...current, driver_contact: event.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Vehicle No.</label>
                <input
                  className="form-control"
                  value={form.vehicle_no}
                  onChange={(event) => setForm((current) => ({ ...current, vehicle_no: event.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Pickup Time</label>
                <input
                  className="form-control"
                  placeholder="09:30 AM"
                  value={form.pickup_time}
                  onChange={(event) => setForm((current) => ({ ...current, pickup_time: event.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Drop Time</label>
                <input
                  className="form-control"
                  placeholder="10:15 AM"
                  value={form.drop_time}
                  onChange={(event) => setForm((current) => ({ ...current, drop_time: event.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Pickup Location</label>
                <input
                  className="form-control"
                  value={form.pickup_location}
                  onChange={(event) => setForm((current) => ({ ...current, pickup_location: event.target.value }))}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Drop Location</label>
                <input
                  className="form-control"
                  value={form.drop_location}
                  onChange={(event) => setForm((current) => ({ ...current, drop_location: event.target.value }))}
                />
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={form.notes}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                className="btn btn-light"
                disabled={saving}
                onClick={() => setEditRow(null)}
              >
                Cancel
              </button>
              <button type="submit" className="add-button" disabled={saving || updatingId}>
                {saving ? "Saving..." : "Update"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
